import type { SortDescriptor } from "react-stately";

import { memo, useState } from "react";

import type { RequestFilterType } from "../../../types/filters";
import type { BaseRequest } from "../../../types/tables";

import { archivedTableConfig } from "../../../config/tables/customer/archivedTableConfig";
import { useArchivedRequests } from "../../../hooks";
import {
  useRequestRowActions,
  useResetFilterOnEmptySearch,
} from "../../../hooks/shared";
import { BulkDuplicateModal } from "../../modals/BulkDuplicateModal";
import { ConfigurableTable } from "../../tables";

interface ArchivedTabProps {
  onFilterChange?: (filter: RequestFilterType) => void;
  searchValue?: string;
  selectedFilter?: RequestFilterType;
}

function ArchivedTabComponent({
  onFilterChange,
  searchValue,
  selectedFilter,
}: ArchivedTabProps) {
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: archivedTableConfig.defaultSort?.column || "archivedAt",
    direction:
      archivedTableConfig.defaultSort?.direction === "desc"
        ? "descending"
        : "ascending",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const { customProps, duplicateModalProps } = useRequestRowActions();

  const {
    data: requestsData,
    isFetching,
    isLoading,
    isSuccess,
  } = useArchivedRequests({
    filter: selectedFilter,
    limit: itemsPerPage,
    page: currentPage,
    search: searchValue,
    sortDescriptor,
  });

  useResetFilterOnEmptySearch({
    // Never the broadest scope ("All" tab is), so always escalate.
    isAlreadyBroadest: false,
    isReady: isSuccess && !isFetching,
    onSwitchToAll: () => onFilterChange?.("all"),
    searchTerm: searchValue,
    totalMatches: requestsData?.total ?? 0,
  });

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const archivedRequests = requestsData?.requests || [];

  return (
    <div className="space-y-6">
      <ConfigurableTable
        config={archivedTableConfig}
        customProps={customProps}
        data={archivedRequests as BaseRequest[]}
        isLoading={isLoading}
        paginationProps={{
          metadata: {
            currentPage,
            itemsPerPage,
            totalItems: requestsData?.total ?? null,
            totalPages: requestsData?.pagination?.totalPages ?? null,
          },
          onItemsPerPageChange: handleItemsPerPageChange,
          onPageChange: handlePageChange,
          pageSizeOptions: [10, 25, 50, 100],
          showPageSizeSelector: true,
        }}
        setSortDescriptor={setSortDescriptor}
        sortDescriptor={sortDescriptor}
        useAdvancedSorting={true}
      />

      <BulkDuplicateModal {...duplicateModalProps} />
    </div>
  );
}

export const ArchivedTab = memo(ArchivedTabComponent);
